/**
 * Subscriber site (#5): newsletter directory (branding-themed, presentation
 * toggles honored) and all-lists view, plus the signed preference centre,
 * double opt-in confirm landing, and one-click unsubscribe. Reads public
 * branding + list views; posts signup and preference changes to the API. There
 * is no login here — the addressium subscriber record is the identity, and any
 * Cognito pool belongs to the org, not to us (docs/ARCHITECTURE.md §4.10).
 */
import { createContext, useContext, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, applyBranding, BRANDING_REVEAL_TIMEOUT_MS, displayError, ORG, preloadLogo, revealPage, type Branding, type PreferenceView, type PublicList } from "./api.js";

type Route =
  | { name: "directory" }
  | { name: "all" }
  | { name: "preferences"; token?: string }
  | { name: "confirm"; token: string }
  | { name: "unsubscribe"; token: string };

function parseRoute(): Route {
  const p = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  if (path.endsWith("/confirm") && p.get("token")) return { name: "confirm", token: p.get("token")! };
  if (path.endsWith("/unsubscribe") && p.get("token")) return { name: "unsubscribe", token: p.get("token")! };
  if (path.endsWith("/preferences")) return { name: "preferences", ...(p.get("token") ? { token: p.get("token")! } : {}) };
  if (path.endsWith("/all")) return { name: "all" };
  return { name: "directory" };
}

/** The org's contact link, if it set one. Only https: and mailto: are ever rendered. */
const ContactContext = createContext<string | undefined>(undefined);
const safeContactUrl = (url: string | undefined): string | undefined =>
  url && /^(https:\/\/|mailto:)/i.test(url) ? url : undefined;

/**
 * What a person sees after signing up. It says the same thing whether the
 * address was new, already confirmed or suppressed (#326), so the contact link
 * is what someone who never receives the email can actually act on.
 */
function SignupSent({ plural, single }: { plural?: boolean; single?: boolean }) {
  const contactUrl = useContext(ContactContext);
  return (
    <p className="muted">
      {/* A single opt-in list sends no confirmation (#361), except to an address
          that opted out before, which still has to confirm. The response is the
          same either way (#326), so the copy covers both. */}
      {single
        ? `Thanks, you're signed up. If we need you to confirm, we'll email you.`
        : `Check your inbox to confirm your subscription${plural ? "s" : ""}.`}
      {contactUrl && (
        <> Trouble receiving a sign-up or forgot-password email? <a href={contactUrl}>Contact us</a>.</>
      )}
    </p>
  );
}

export function App() {
  const route = useMemo(parseRoute, []);
  const [branding, setBranding] = useState<Branding | null>(null);
  useEffect(() => {
    // The page is hidden until this settles (index.html), so the default brand
    // never shows before the org's. The timeout bounds the wait.
    const timeout = window.setTimeout(revealPage, BRANDING_REVEAL_TIMEOUT_MS);
    api.branding()
      .then(async (b) => { await preloadLogo(b); setBranding(b); applyBranding(b); })
      .catch(() => undefined)
      .finally(() => { window.clearTimeout(timeout); revealPage(); });
    return () => window.clearTimeout(timeout);
  }, []);

  return (
    <ContactContext.Provider value={safeContactUrl(branding?.contactUrl)}>
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="Home">
          {branding?.logoUrl && <img src={branding.logoUrl} alt="" />}
          <span>Newsletters</span>
        </a>
        {(route.name === "directory" || route.name === "all" || route.name === "preferences") && (
          <nav aria-label="Newsletter navigation">
            <a className={route.name === "directory" ? "active" : ""} href="/">Browse</a>
            <a className={route.name === "all" ? "active" : ""} href="/all">Subscribe to all</a>
            <a className={route.name === "preferences" ? "active" : ""} href="/preferences">Manage subscriptions</a>
          </nav>
        )}
      </header>
      {(route.name === "directory" || route.name === "all") && (
        <section className="hero">
          <p className="eyebrow">Stay in the loop</p>
          <h1>Ideas worth opening.</h1>
          <p>Choose the newsletters you want in your inbox. No account required — just a thoughtful email when it matters.</p>
        </section>
      )}
      {(route.name === "directory" || route.name === "all") && (
        <main>
          {route.name === "directory" && <Directory />}
          {route.name === "all" && <AllNewsletters />}
        </main>
      )}
      {route.name === "confirm" && <main className="action-page"><Confirm token={route.token} /></main>}
      {route.name === "unsubscribe" && <main className="action-page"><Unsubscribe token={route.token} /></main>}
      {route.name === "preferences" && <main className="action-page"><Preferences token={route.token} /></main>}
      <footer>You can unsubscribe at any time.</footer>
    </div>
    </ContactContext.Provider>
  );
}

function NewsletterMetadata({ list }: { list: { category?: string; sendFrequency?: string; frequencyLabel?: string; presentation?: { showFrequency: boolean } } }) {
  const frequency = list.sendFrequency || (list.presentation?.showFrequency ? list.frequencyLabel : undefined);
  if (!list.category && !frequency) return null;
  return <span className="newsletter-metadata">{list.category && <span className="pill">{list.category}</span>}{frequency && <span className="pill">{frequency}</span>}</span>;
}

export function Preferences({ token }: { token?: string }) {
  const [view, setView] = useState<PreferenceView | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    setBusy(true);
    api.preferences(token)
      .then((next) => {
        setView(next);
        setChecked(Object.fromEntries(next.rows.map((row) => [row.listId, row.subscribed])));
      })
      .catch((e) => setError(displayError(e)))
      .finally(() => setBusy(false));
  }, [token]);

  const request = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api.requestPreferences(email);
      setMessage(result.message);
      setEmail("");
    } catch (e) { setError(displayError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!token || !view) return;
    setBusy(true); setError(""); setMessage("");
    const changes = view.rows
      .filter((row) => checked[row.listId] !== row.subscribed)
      .map((row) => ({ listId: row.listId, subscribed: checked[row.listId] === true }));
    try {
      const result = await api.updatePreferences(token, changes);
      setView(result.view);
      setChecked(Object.fromEntries(result.view.rows.map((row) => [row.listId, row.subscribed])));
      setMessage(result.rejected.length ? "Some changes could not be applied." : "Your preferences have been saved.");
    } catch (e) { setError(displayError(e)); }
    finally { setBusy(false); }
  };

  if (!token) return (
    <div className="action-card preference-card">
      <span className="action-icon">✦</span>
      <div className="title">Manage your subscriptions</div>
      <p className="muted">Enter your email and we’ll send a private link to update every newsletter subscription.</p>
      <form className="preference-request" onSubmit={(e) => void request(e)}>
        <input type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
        <button disabled={busy}>{busy ? "Sending…" : "Email me a link"}</button>
      </form>
      {message && <p className="muted">{message}</p>}
      {error && <p className="err">{error}</p>}
    </div>
  );

  if (error && !view) return <div className="action-card preference-card"><div className="title">This link could not be opened</div><p className="err">{error}</p><a href="/preferences">Request a new link</a></div>;
  if (!view) return <div className="action-card preference-card"><p className="muted">Loading your preferences…</p></div>;

  return (
    <div className="action-card preference-card preference-editor">
      <span className="action-icon">✦</span>
      <div className="title">Your subscriptions</div>
      <p className="muted">Manage newsletters for <strong>{view.email}</strong>.</p>
      {view.rows.length === 0 && <p className="muted">There are no newsletters available for this address.</p>}
      <div className="preference-list">
        {view.rows.map((row) => (
          <label className="preference-row" key={row.listId}>
            <input type="checkbox" checked={checked[row.listId] === true} onChange={(e) => setChecked((current) => ({ ...current, [row.listId]: e.target.checked }))} disabled={busy || row.status === "bounced" || row.status === "complained"} />
            <span><b>{row.name}</b><NewsletterMetadata list={row} />{row.description && <small>{row.description}</small>}{(row.status === "bounced" || row.status === "complained") && <small className="err">This address cannot be resubscribed.</small>}{row.status === "sunset" && <small>Paused because it had gone unread for a while. Tick to resubscribe.</small>}</span>
          </label>
        ))}
      </div>
      <button onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save preferences"}</button>
      {message && <p className="muted">{message}</p>}
      {error && <p className="err">{error}</p>}
      <a href="/">Back to newsletters</a>
    </div>
  );
}

/**
 * "All newsletters" landing page (#61): browse every list, check the ones you
 * want, enter one email, and opt into them all with a single double opt-in — no
 * account or login required.
 */
function AllNewsletters() {
  const [lists, setLists] = useState<PublicList[]>([]);
  // Distinct from `lists.length === 0`. An org with no public newsletters is a
  // normal state, and conflating it with "still fetching" left the page saying
  // "Loading newsletters…" forever — including on a stale build pointed at an
  // org that no longer exists, where the request succeeds and returns [].
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    // One request. This used to fan out to /lists/{id}/public per list, which
    // made the front page's cost scale with the number of newsletters.
    api
      .directory()
      .then(setLists)
      .catch((e) => setErr(displayError(e)))
      .finally(() => setLoaded(true));
  }, []);
  const toggle = (id: string) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [sent, setSent] = useState(false);
  const [sentSingle, setSentSingle] = useState(false);
  const [busy, setBusy] = useState(false);
  const subscribe = async () => {
    setBusy(true); setMsg(""); setErr(""); setSent(false);
    try {
      await api.signupMany(email, [...selected]);
      setSentSingle(lists.filter((l) => selected.has(l.listId)).every((l) => l.optInPolicy === "single"));
      setSent(true);
      setSelected(new Set()); setEmail("");
    } catch (e) { setErr(displayError(e)); }
    finally { setBusy(false); }
  };
  if (err) return <p className="err">{err}</p>;
  if (!ORG) return <p className="muted">Set VITE_ORG_ID to view this org's newsletters.</p>;
  return (
    <section className="subscribe-panel">
      <div className="section-heading"><div><p className="eyebrow">One simple signup</p><h2>Choose your inbox</h2></div><span className="count">{lists.length} available</span></div>
      <p className="muted">Pick the ones you'd like, add your email, and confirm once.</p>
      {!loaded && <p className="muted">Loading newsletters…</p>}
      {loaded && lists.length === 0 && (
        <p className="muted">No newsletters are published yet.</p>
      )}
      {lists.map((l) => (
        <label key={l.listId} className="choice">
          <input type="checkbox" checked={selected.has(l.listId)} onChange={() => toggle(l.listId)} />
          <span className="choice-copy">
            <b>{l.name}</b>
            <NewsletterMetadata list={l} />
            {l.description && <div className="muted">{l.description}</div>}
          </span>
        </label>
      ))}
      <div className="signup-row">
        <input inputMode="email" autoComplete="email" aria-label="Email address" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button className="primary" onClick={() => void subscribe()} disabled={busy || !email || selected.size === 0}>
          {busy ? "Subscribing…" : `Subscribe${selected.size > 0 ? ` (${selected.size})` : ""}`}
        </button>
      </div>
      {sent && <SignupSent plural single={sentSingle} />}
      {msg && <p className="muted">{msg}</p>}
    </section>
  );
}

function Directory() {
  const [ids, setIds] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api
      .directory()
      .then((ls) => setIds(ls.map((l) => l.listId)))
      .catch((e) => setErr(displayError(e)))
      .finally(() => setLoaded(true));
  }, []);
  if (err) return <p className="err">{err}</p>;
  if (!ORG) return <p className="muted">Set VITE_ORG_ID to view this org's newsletters.</p>;
  return (
    <div className="directory">
      {!loaded && <p className="muted">Loading newsletters…</p>}
      {loaded && ids.length === 0 && (
        <p className="muted">No newsletters are published yet.</p>
      )}
      <div className="list-grid">{ids.map((id) => <ListCard key={id} listId={id} email={email} onEmailChange={setEmail} />)}</div>
    </div>
  );
}

function ListCard({ listId, email, onEmailChange }: { listId: string; email: string; onEmailChange: (email: string) => void }) {
  const [list, setList] = useState<PublicList | null>(null);
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.publicList(listId).then(setList).catch(() => undefined);
  }, [listId]);
  const subscribe = async () => {
    setBusy(true); setMsg(""); setSent(false);
    try { await api.signup(email, listId); setSent(true); }
    catch (e) { setMsg(displayError(e)); }
    finally { setBusy(false); }
  };
  if (!list) return null;
  return (
    <article className="list-card">
      <div className="list-card-top"><span className="list-icon">✦</span><div className="title">{list.name}</div></div>
      <div className="meta">
        <NewsletterMetadata list={list} />
        {list.presentation.showSendTime && list.sendTimeLabel && <span>{list.sendTimeLabel}</span>}
      </div>
      {list.description && <p className="muted">{list.description}</p>}
      <div className="signup-row">
        <input inputMode="email" autoComplete="email" aria-label={`Email address for ${list.name}`} placeholder="you@example.com" value={email} onChange={(e) => onEmailChange(e.target.value)} />
        <button className="primary" onClick={() => void subscribe()} disabled={busy || !email}>{busy ? "Subscribing…" : "Subscribe"}</button>
      </div>
      {sent && <SignupSent single={list.optInPolicy === "single"} />}
      {msg && <p className="muted">{msg}</p>}
    </article>
  );
}

function Confirm({ token }: { token: string }) {
  const [state, setState] = useState("Confirm you want to subscribe.");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const confirm = async () => {
    setBusy(true); setFailed(false);
    try {
      const result = await api.confirm(token);
      setState(result.confirmed ? "You're subscribed — thank you!" : "This link has already been used or is no longer pending.");
      setDone(true);
    } catch (error) { setState(displayError(error)); setFailed(true); }
    finally { setBusy(false); }
  };
  // A failure reads as one (red, like Unsubscribe's), and the button becomes a
  // retry rather than offering the same "Confirm subscription" as if nothing happened.
  return <div className="action-card"><span className="action-icon">✓</span><div className="title">Confirm subscription</div><p className={failed ? "err" : "muted"}>{state}</p>{!done && <button disabled={busy} onClick={() => void confirm()}>{busy ? "Confirming…" : failed ? "Try again" : "Confirm subscription"}</button>}{" "}<a href="/" style={{ marginLeft: 12 }}>Back to newsletters</a></div>;
}

function Unsubscribe({ token }: { token: string }) {
  const [state, setState] = useState<"idle" | "done" | "error">("idle");
  const [err, setErr] = useState("");
  const go = async () => {
    try { await api.unsubscribe(token); setState("done"); } catch (e) { setErr(displayError(e)); setState("error"); }
  };
  return (
    <div className="action-card">
      <span className="action-icon">↗</span>
      <div className="title">Unsubscribe</div>
      {state === "idle" && <><p className="muted">Confirm you want to unsubscribe from this list.</p><button onClick={() => void go()}>Unsubscribe</button></>}
      {state === "done" && <p className="muted">You've been unsubscribed.</p>}
      {state === "error" && <p className="err">{err}</p>}
      {state !== "idle" && <a href="/">Back to newsletters</a>}
    </div>
  );
}
